import classNames from 'classnames';
import { ChangeEvent, HTMLInputTypeAttribute, useEffect, useState } from 'react';
import { z, ZodError } from 'zod';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type Props = {
  label: string;
  defaultValue: string | null;
  onChange: (value: string) => void;
  validationSchema?: z.ZodType;
  type?: HTMLInputTypeAttribute;
};

export const EditableItem = ({ label, defaultValue, onChange, validationSchema, type }: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setInputValue(event.target.value);

    if (validationSchema && event.target.value.toString().trim() !== '') {
      try {
        validationSchema.parse(event.target.value);
        setError(null);
      } catch (err) {
        if (err instanceof ZodError) {
          setError(err.errors[0].message);
          return;
        }
      }
    }

    setError(null);
    onChange(event.target.value);
  };

  useEffect(() => {
    setInputValue(defaultValue || '');
  }, [defaultValue]);

  return (
    <TooltipProvider>
      <Tooltip open={error ? undefined : false}>
        <section className="grid grid-cols-2 items-center leading-5 tracking-wide gap-x-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <TooltipTrigger>
            <Input
              size="xs"
              type={type || 'text'}
              data-1p-ignore="true"
              value={inputValue}
              onChange={handleOnChange}
              placeholder="+ Add"
              error={!!error}
              className={classNames(
                'ml-[-8px] rounded-md border text-sm transition duration-200 ease-in-out focus:outline-none focus-within:!border-foreground focus-within:ring-2 focus-within:ring-ring/25 hover:!border-foreground-subtle',
                {
                  '!border-destructive hover:!border-destructive': !!error,
                  '!border-transparent': !error,
                },
              )}
            />
          </TooltipTrigger>
        </section>
        <TooltipContent>{error}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
